import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Download, FileText, FileSpreadsheet, File, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { exporter } from '../../utils/exportRapport';
import { getDeviseCourante } from '../../utils/format';
import DateRangePicker from '../../components/common/DateRangePicker';
import { getPeriodRange } from '../../components/common/PeriodPills';

// ── Helpers ───────────────────────────────────────────────────
const fmt = n => Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

// ── Bouton d'export avec menu déroulant ──────────────────────
function ExportMenu({ rapport }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(null); // format en cours
  const ref = useRef(null);

  // Fermer si clic extérieur
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const formats = [
    { key: 'pdf',  label: 'PDF',          icon: <File size={14} />,            color: 'text-red-600' },
    { key: 'xlsx', label: 'Excel (.xlsx)', icon: <FileSpreadsheet size={14} />, color: 'text-green-600' },
    { key: 'csv',  label: 'CSV',           icon: <FileText size={14} />,        color: 'text-blue-600' },
    { key: 'word', label: 'Word (.doc)',   icon: <FileText size={14} />,        color: 'text-indigo-600' },
  ];

  const handleExport = async (format) => {
    setOpen(false);
    setLoading(format);
    await exporter(format, rapport, (msg) => alert('Erreur export : ' + msg));
    setLoading(null);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={!!loading}
        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60 shadow-sm"
      >
        <Download size={15} />
        {loading ? `Export ${loading.toUpperCase()}…` : 'Télécharger'}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Choisir le format</p>
          </div>
          {formats.map(f => (
            <button
              key={f.key}
              onClick={() => handleExport(f.key)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700 transition-colors ${f.color}`}
            >
              {f.icon}
              <span className="text-gray-800">{f.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Composant card stat ───────────────────────────────────────
// Affiche une métrique avec optionnellement une variation par rapport à la
// période précédente (% en vert si hausse, rouge si baisse).
function StatCard({ label, value, sub, color = 'indigo', variation, inverseSens = false }) {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    green:  'bg-green-50 text-green-700 border-green-200',
    amber:  'bg-amber-50 text-amber-700 border-amber-200',
    red:    'bg-red-50 text-red-700 border-red-200',
  };

  // inverseSens : pour les dépenses, une HAUSSE est mauvaise (rouge)
  // alors qu'une hausse de CA est bonne (vert). On inverse le code couleur.
  const variationEstBonne = inverseSens ? variation < 0 : variation > 0;
  const variationEstMauvaise = inverseSens ? variation > 0 : variation < 0;

  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      <div className="flex items-center gap-2 mt-1">
        {sub && <p className="text-xs opacity-60">{sub}</p>}
        {variation !== undefined && variation !== null && (
          <span className={`text-xs font-bold ${
            variationEstBonne   ? 'text-green-600' :
            variationEstMauvaise ? 'text-red-600' :
            'text-gray-500'
          }`}>
            {variation > 0 ? '↑ +' : variation < 0 ? '↓ ' : '→ '}{variation}% vs précédent
          </span>
        )}
      </div>
    </div>
  );
}

// ── Composant filtre date ─────────────────────────────────────
// Wrapper minimal sur DateRangePicker pour conserver la signature historique
// `onChange('debut'|'fin', value)` utilisée par handleDate ailleurs dans le
// composant Rapports — pas de refonte intrusive du reste.
function DateFilter({ debut, fin, onChange }) {
  return (
    <DateRangePicker
      debut={debut}
      fin={fin}
      onChange={(d, f) => {
        if (d !== debut) onChange('debut', d);
        if (f !== fin)   onChange('fin', f);
      }}
    />
  );
}

// ── En-tête d'onglet avec titre + bouton export ───────────────
function TabHeader({ titre, rapport }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-bold text-gray-800">{titre}</h2>
      {rapport && <ExportMenu rapport={rapport} />}
    </div>
  );
}

// ── Onglet CA ─────────────────────────────────────────────────
function OngletCA({ debut, fin }) {
  const { moduleActif } = useAuth();
  const aTerrainCA = moduleActif('vendeurs_terrain');
  const aLigneCA   = moduleActif('commandes_ligne');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/rapports/ca?date_debut=${debut}&date_fin=${fin}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [debut, fin]);

  if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;
  if (!data)   return <div className="text-center py-20 text-red-400">Erreur de chargement</div>;

  const pieData = [
    { name: 'Magasin',   value: data.parCanal.magasin },
    { name: 'Terrain',   value: data.parCanal.terrain },
    { name: 'Livraison', value: data.parCanal.livraison },
  ].filter(d => d.value > 0);

  const evolutionFormatted = (data.evolution || []).map(e => ({
    date: e.date?.slice(5), Magasin: Number(e.magasin), Terrain: Number(e.terrain),
    Livraison: Number(e.livraison), Total: Number(e.total),
  }));

  // ── Rapport pour l'export
  const rapport = {
    titre:   "Chiffre d'affaires",
    periode: { debut, fin },
    sections: [
      {
        titre: 'Résumé',
        colonnes: [{ header: 'Indicateur', key: 'label' }, { header: 'Valeur', key: 'valeur' }],
        lignes: [
          { label: 'CA Total',           valeur: `${fmt(data.totalCA)}` },
          { label: 'Total Dépenses',     valeur: `${fmt(data.totalDepenses)}` },
          { label: 'Bénéfice net',       valeur: `${fmt(data.beneficeNet)}` },
          { label: 'Jours ouvrés',       valeur: data.joursOuvres },
          { label: 'Moyenne / jour',     valeur: `${fmt(data.moyenneParJour)}` },
        ],
      },
      {
        titre: 'CA par canal',
        colonnes: [
          { header: 'Canal',   key: 'canal' },
          { header: 'Montant', key: 'montant' },
          { header: '% du CA', key: 'pct' },
        ],
        lignes: [
          { canal: 'Magasin',   montant: `${fmt(data.parCanal.magasin)}`,   pct: data.totalCA > 0 ? `${Math.round(data.parCanal.magasin/data.totalCA*100)}%` : '0%' },
          { canal: 'Terrain',   montant: `${fmt(data.parCanal.terrain)}`,   pct: data.totalCA > 0 ? `${Math.round(data.parCanal.terrain/data.totalCA*100)}%` : '0%' },
          { canal: 'Livraison', montant: `${fmt(data.parCanal.livraison)}`, pct: data.totalCA > 0 ? `${Math.round(data.parCanal.livraison/data.totalCA*100)}%` : '0%' },
        ],
      },
      ...(data.evolution?.length > 0 ? [{
        titre: 'Évolution journalière',
        colonnes: [
          { header: 'Date',      key: 'date' },
          { header: 'Magasin',   key: 'magasin' },
          { header: 'Terrain',   key: 'terrain' },
          { header: 'Livraison', key: 'livraison' },
          { header: 'Total',     key: 'total' },
        ],
        lignes: (data.evolution || []).map(e => ({
          date:      e.date,
          magasin:   `${fmt(e.magasin)}`,
          terrain:   `${fmt(e.terrain)}`,
          livraison: `${fmt(e.livraison)}`,
          total:     `${fmt(e.total)}`,
        })),
      }] : []),
    ],
  };

  return (
    <div className="space-y-6">
      <TabHeader titre="Chiffre d'affaires" rapport={rapport} />

      {/* Bandeau d'avertissement : le "bénéfice net" affiché est en réalité
          un résultat de trésorerie (entrées − sorties caisse), pas un vrai
          bénéfice comptable. */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-3">
        <span className="text-base">⚠️</span>
        <div>
          <strong>Bénéfice net = résultat de trésorerie</strong> (entrées − sorties caisse de la période).
          Cela <strong>n'inclut pas le coût d'achat des produits vendus</strong> (COGS) ni la variation
          du stock. Pour un bénéfice comptable réel, il faudrait calculer la marge par article
          (prochaine amélioration prévue : marge brute par produit).
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="CA Total"
          value={`${fmt(data.totalCA)}`}
          color="indigo"
          variation={data.variations?.totalCA}
        />
        <StatCard
          label="Dépenses"
          value={`${fmt(data.totalDepenses)}`}
          color="red"
          variation={data.variations?.totalDepenses}
          inverseSens   /* hausse de dépenses = mauvais */
        />
        <StatCard
          label="Bénéfice net"
          value={`${fmt(data.beneficeNet)}`}
          color="green"
          variation={data.variations?.beneficeNet}
        />
        <StatCard
          label="Moy. / jour"
          value={`${fmt(data.moyenneParJour)}`}
          sub={`${data.joursOuvres} jour(s)`}
          color="amber"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Magasin',   val: data.parCanal.magasin,   color: 'indigo', visible: true },
          { label: 'Terrain',   val: data.parCanal.terrain,   color: 'amber',  visible: aTerrainCA },
          { label: 'Livraison', val: data.parCanal.livraison, color: 'green',  visible: aLigneCA },
        ].filter(c => c.visible).map(c => (
          <div key={c.label} className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">{c.label}</p>
            <p className="text-xl font-bold text-gray-800 mt-1">{fmt(c.val)}</p>
            {data.totalCA > 0 && (
              <>
                <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${c.color === 'indigo' ? 'bg-indigo-500' : c.color === 'amber' ? 'bg-amber-500' : 'bg-green-500'}`}
                    style={{ width: `${Math.round((c.val / data.totalCA) * 100)}%` }} />
                </div>
                <p className="text-xs text-gray-400 mt-1">{Math.round((c.val / data.totalCA) * 100)}% du CA</p>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {pieData.length > 0 && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Répartition CA par canal</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => `${fmt(v)}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        {evolutionFormatted.length > 0 && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Évolution journalière du CA</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={evolutionFormatted}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => `${fmt(v)}`} />
                <Legend />
                <Bar dataKey="Magasin"   fill="#6366f1" stackId="a" />
                {aTerrainCA && <Bar dataKey="Terrain"   fill="#f59e0b" stackId="a" />}
                {aLigneCA   && <Bar dataKey="Livraison" fill="#10b981" stackId="a" radius={[4,4,0,0]} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Onglet Bénéfice ───────────────────────────────────────────
function OngletBenefice({ debut, fin }) {
  const { moduleActif } = useAuth();
  const aTerrainB = moduleActif('vendeurs_terrain');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/rapports/benefice?date_debut=${debut}&date_fin=${fin}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [debut, fin]);

  if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;
  if (!data)   return <div className="text-center py-20 text-red-400">Erreur de chargement</div>;

  const parMoisFormatted = (data.parMois || []).map(m => ({
    mois: m.mois?.slice(5), CA: Number(m.ca), Dépenses: Number(m.depenses), Bénéfice: Number(m.benefice),
  }));

  const waterfall = [
    { name: 'CA Ventes',     valeur: data.caVentes,           type: 'positif' },
    { name: 'Achats fourn.', valeur: -data.achatsFournisseurs, type: 'negatif' },
    { name: 'Dép. diverses', valeur: -data.depensesDiverses,   type: 'negatif' },
    ...(aTerrainB ? [{ name: 'Écarts vend.', valeur: -data.ecartsVendeurs, type: 'negatif' }] : []),
    { name: 'Bénéfice net',  valeur: data.beneficeNet,         type: data.beneficeNet >= 0 ? 'resultat' : 'perte' },
  ];

  // ── Rapport pour l'export
  const rapport = {
    titre:   'Bénéfice net',
    periode: { debut, fin },
    sections: [
      {
        titre: 'Résumé bénéfice',
        colonnes: [{ header: 'Indicateur', key: 'label' }, { header: 'Montant', key: 'valeur' }],
        lignes: [
          { label: 'CA Ventes',             valeur: `${fmt(data.caVentes)}` },
          { label: 'Achats fournisseurs',   valeur: `- ${fmt(data.achatsFournisseurs)}` },
          { label: 'Dépenses diverses',     valeur: `- ${fmt(data.depensesDiverses)}` },
          ...(aTerrainB ? [{ label: 'Écarts vendeurs', valeur: `- ${fmt(data.ecartsVendeurs)}` }] : []),
          { label: 'Bénéfice net',          valeur: `${fmt(data.beneficeNet)}` },
          { label: 'Marge',                 valeur: `${data.margePercent}%` },
        ],
      },
      ...(data.parMois?.length > 0 ? [{
        titre: 'Évolution mensuelle',
        colonnes: [
          { header: 'Mois',      key: 'mois' },
          { header: 'CA',        key: 'ca' },
          { header: 'Dépenses',  key: 'depenses' },
          { header: 'Bénéfice',  key: 'benefice' },
        ],
        lignes: (data.parMois || []).map(m => ({
          mois:     m.mois,
          ca:       `${fmt(m.ca)}`,
          depenses: `${fmt(m.depenses)}`,
          benefice: `${fmt(m.benefice)}`,
        })),
      }] : []),
    ],
  };

  return (
    <div className="space-y-6">
      <TabHeader titre="Bénéfice net" rapport={rapport} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="CA Ventes"           value={`${fmt(data.caVentes)}`}           color="indigo" />
        <StatCard label="Achats fournisseurs" value={`${fmt(data.achatsFournisseurs)}`}  color="red" />
        <StatCard label="Dépenses diverses"   value={`${fmt(data.depensesDiverses)}`}    color="amber" />
        <StatCard label="Bénéfice net"        value={`${fmt(data.beneficeNet)}`}         sub={`Marge: ${data.margePercent}%`} color={data.beneficeNet >= 0 ? 'green' : 'red'} />
      </div>

      <div className="bg-white rounded-xl border p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Décomposition du bénéfice</h3>
        <div className="space-y-3">
          {waterfall.map(item => (
            <div key={item.name} className="flex items-center gap-3">
              <span className="text-sm text-gray-600 w-36 shrink-0">{item.name}</span>
              <div className="flex-1 h-7 bg-gray-50 rounded-lg overflow-hidden">
                <div className={`h-full rounded-lg transition-all ${
                  item.type === 'positif' ? 'bg-indigo-500' : item.type === 'negatif' ? 'bg-red-400' :
                  item.type === 'resultat' ? 'bg-green-500' : 'bg-red-600'}`}
                  style={{ width: `${data.caVentes > 0 ? Math.min(100, Math.abs(item.valeur)/data.caVentes*100) : 0}%` }} />
              </div>
              <span className={`text-sm font-semibold w-32 text-right ${item.valeur >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                {item.valeur >= 0 ? '+' : ''}{fmt(item.valeur)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {parMoisFormatted.length > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Évolution mensuelle CA vs Dépenses</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={parMoisFormatted}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v => `${fmt(v)}`} />
              <Legend />
              <Line type="monotone" dataKey="CA"       stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Dépenses" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Bénéfice" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ── Onglet Produits ───────────────────────────────────────────
function OngletProduits({ debut, fin }) {
  const { moduleActif } = useAuth();
  const aTerrainP = moduleActif('vendeurs_terrain');
  const aLigneP   = moduleActif('commandes_ligne');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  // ── Filtres catégorie / produit ──────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [produits, setProduits]     = useState([]);
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [filtreProduit, setFiltreProduit]     = useState('');

  // Charger une fois la liste des catégories + produits pour les filtres
  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data || [])).catch(() => {});
    api.get('/produits?limit=500').then(r => setProduits(r.data.produits || [])).catch(() => {});
  }, []);

  // Réinitialiser le produit sélectionné si on change de catégorie.
  // On utilise refs pour pointer sur les valeurs actuelles sans mettre
  // produits/filtreProduit en dépendances (sinon boucle ou warning).
  const refProduits      = useRef(produits);
  const refFiltreProduit = useRef(filtreProduit);
  useEffect(() => { refProduits.current = produits; });
  useEffect(() => { refFiltreProduit.current = filtreProduit; });
  useEffect(() => {
    const fp = refFiltreProduit.current;
    if (filtreCategorie && fp) {
      const p = refProduits.current.find(x => x.id === fp);
      if (p && p.categorie_id !== filtreCategorie) setFiltreProduit('');
    }
  }, [filtreCategorie]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ date_debut: debut, date_fin: fin, limit: 15 });
    if (filtreCategorie) params.append('categorie_id', filtreCategorie);
    if (filtreProduit)   params.append('produit_id',   filtreProduit);
    api.get(`/rapports/produits?${params}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [debut, fin, filtreCategorie, filtreProduit]);

  // Produits affichables dans le select (filtrés par catégorie si choisie)
  const produitsAffichables = filtreCategorie
    ? produits.filter(p => p.categorie_id === filtreCategorie)
    : produits;

  if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;
  if (!data)   return <div className="text-center py-20 text-red-400">Erreur de chargement</div>;

  const chartData = (data.topProduits || []).slice(0, 10).map(p => ({
    name: p.produit_nom?.length > 14 ? p.produit_nom.slice(0, 14) + '…' : p.produit_nom,
    Magasin: Number(p.qte_magasin), Terrain: Number(p.qte_terrain), Livraison: Number(p.qte_livraison),
  }));

  // ── Rapport pour l'export
  const rapport = {
    titre:   'Top Produits vendus',
    periode: { debut, fin },
    sections: [
      {
        titre: `Classement produits (total : ${fmt(data.totalArticlesVendus)} articles)`,
        colonnes: [
          { header: '#',          key: 'rang' },
          { header: 'Produit',    key: 'produit' },
          { header: 'Catégorie',  key: 'categorie' },
          { header: 'Magasin',    key: 'magasin' },
          { header: 'Terrain',    key: 'terrain' },
          { header: 'Livraison',  key: 'livraison' },
          { header: 'Total',      key: 'total' },
        ],
        lignes: (data.topProduits || []).map((p, i) => ({
          rang:      i + 1,
          produit:   p.produit_nom,
          categorie: p.categorie,
          magasin:   Number(p.qte_magasin),
          terrain:   Number(p.qte_terrain),
          livraison: Number(p.qte_livraison),
          total:     Number(p.quantite_vendue),
        })),
      },
      ...(data.topVariantes?.length > 0 ? [{
        titre: 'Top 10 variantes',
        colonnes: [
          { header: '#',          key: 'rang' },
          { header: 'Produit',    key: 'produit' },
          { header: 'Modèle',     key: 'modele' },
          { header: 'Couleur',    key: 'couleur' },
          { header: 'Taille',     key: 'taille' },
          { header: 'Qté vendue', key: 'qte' },
        ],
        lignes: data.topVariantes.map((v, i) => ({
          rang:    i + 1,
          produit: v.produit_nom,
          modele:  v.modele || '—',
          couleur: v.couleur || '—',
          taille:  v.taille,
          qte:     Number(v.quantite_vendue),
        })),
      }] : []),
    ],
  };

  return (
    <div className="space-y-6">
      <TabHeader titre="Top Produits" rapport={rapport} />

      {/* Filtres catégorie + produit */}
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Catégorie</label>
          <select
            value={filtreCategorie}
            onChange={e => setFiltreCategorie(e.target.value)}
            className="w-full bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
          >
            <option value="">Toutes les catégories</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[140px] sm:min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Produit</label>
          <select
            value={filtreProduit}
            onChange={e => setFiltreProduit(e.target.value)}
            className="w-full bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
          >
            <option value="">Tous les produits</option>
            {produitsAffichables.map(p => (
              <option key={p.id} value={p.id}>{p.nom}</option>
            ))}
          </select>
        </div>
        {(filtreCategorie || filtreProduit) && (
          <button
            type="button"
            onClick={() => { setFiltreCategorie(''); setFiltreProduit(''); }}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            ✕ Effacer les filtres
          </button>
        )}
      </div>

      <StatCard
        label="Total articles vendus (NET, retours vendeur déduits)"
        value={fmt(data.totalArticlesVendus)}
        color="indigo"
      />

      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Top produits par canal (quantités)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Magasin"   fill="#6366f1" stackId="a" />
              {aTerrainP && <Bar dataKey="Terrain"   fill="#f59e0b" stackId="a" />}
              {aLigneP   && <Bar dataKey="Livraison" fill="#10b981" stackId="a" radius={[0,4,4,0]} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Classement complet</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b bg-gray-50">
                <th className="px-4 py-2 text-left">#</th>
                <th className="px-4 py-2 text-left">Produit</th>
                <th className="px-4 py-2 text-left">Catégorie</th>
                <th className="px-4 py-2 text-center">Magasin</th>
                {aTerrainP && <th className="px-4 py-2 text-center">Terrain</th>}
                {aLigneP   && <th className="px-4 py-2 text-center">Livraison</th>}
                <th className="px-4 py-2 text-center font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.topProduits || []).map((p, i) => (
                <tr key={p.produit_id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-400">{i+1}</td>
                  <td className="px-4 py-2 font-medium text-gray-800">{p.produit_nom}</td>
                  <td className="px-4 py-2 text-gray-500">{p.categorie}</td>
                  <td className="px-4 py-2 text-center">{p.qte_magasin}</td>
                  {aTerrainP && <td className="px-4 py-2 text-center">{p.qte_terrain}</td>}
                  {aLigneP   && <td className="px-4 py-2 text-center">{p.qte_livraison}</td>}
                  <td className="px-4 py-2 text-center font-bold text-indigo-700">{Number(p.quantite_vendue)}</td>
                </tr>
              ))}
              {!(data.topProduits?.length) && (
                <tr><td colSpan={5 + (aTerrainP ? 1 : 0) + (aLigneP ? 1 : 0)} className="px-4 py-8 text-center text-gray-400">Aucune vente sur la période</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(data.topVariantes || []).length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Top 10 variantes</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b bg-gray-50">
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Produit</th>
                  <th className="px-4 py-2 text-left">Modèle</th>
                  <th className="px-4 py-2 text-left">Couleur</th>
                  <th className="px-4 py-2 text-left">Taille</th>
                  <th className="px-4 py-2 text-center">Qté vendue</th>
                </tr>
              </thead>
              <tbody>
                {data.topVariantes.map((v, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-400">{i+1}</td>
                    <td className="px-4 py-2 font-medium">{v.produit_nom}</td>
                    <td className="px-4 py-2">{v.modele || '—'}</td>
                    <td className="px-4 py-2">{v.couleur || '—'}</td>
                    <td className="px-4 py-2">{v.taille}</td>
                    <td className="px-4 py-2 text-center font-bold text-indigo-700">{Number(v.quantite_vendue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Vendeurs ───────────────────────────────────────────
function OngletVendeurs({ debut, fin }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/rapports/vendeurs?date_debut=${debut}&date_fin=${fin}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [debut, fin]);

  if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;
  if (!data)   return <div className="text-center py-20 text-red-400">Erreur de chargement</div>;

  const chartData = (data.performance || [])
    .filter(v => Number(v.montant_remis_total) > 0)
    .map(v => ({ name: v.prenom, Remis: Number(v.montant_remis_total), Attendu: Number(v.montant_attendu_total) }));

  // ── Rapport pour l'export
  const rapport = {
    titre:   'Performance Vendeurs',
    periode: { debut, fin },
    sections: [
      {
        titre: `Performance individuelle (total encaissé : ${fmt(data.totalEncaisse)})`,
        colonnes: [
          { header: 'Vendeur',          key: 'vendeur' },
          { header: 'Téléphone',        key: 'telephone' },
          { header: 'Jours travaillés', key: 'jours' },
          { header: 'Montant attendu',  key: 'attendu' },
          { header: 'Montant remis',    key: 'remis' },
          { header: 'Écarts (-)',       key: 'ecarts' },
          { header: 'Fiabilité',        key: 'fiabilite' },
          { header: 'Dette',            key: 'dette' },
        ],
        lignes: (data.performance || []).map(v => ({
          vendeur:    `${v.prenom} ${v.nom}`,
          telephone:  v.telephone || '—',
          jours:      v.jours_travailles,
          attendu:    `${fmt(v.montant_attendu_total)}`,
          remis:      `${fmt(v.montant_remis_total)}`,
          ecarts:     v.ecarts_negatifs > 0 ? `- ${fmt(v.ecarts_negatifs)}` : '—',
          fiabilite:  `${v.taux_fiabilite}%`,
          dette:      v.solde_dette > 0 ? `${fmt(v.solde_dette)}` : '—',
        })),
      },
    ],
  };

  return (
    <div className="space-y-6">
      <TabHeader titre="Performance Vendeurs" rapport={rapport} />
      <StatCard label="Total encaissé (vendeurs terrain)" value={`${fmt(data.totalEncaisse)}`} color="indigo" />

      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Montants attendus vs remis</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v => `${fmt(v)}`} />
              <Legend />
              <Bar dataKey="Attendu" fill="#e0e7ff" radius={[4,4,0,0]} />
              <Bar dataKey="Remis"   fill="#6366f1" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Performance individuelle</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b bg-gray-50">
                <th className="px-4 py-2 text-left">Vendeur</th>
                <th className="px-4 py-2 text-center">Jours</th>
                <th className="px-4 py-2 text-right">Attendu</th>
                <th className="px-4 py-2 text-right">Remis</th>
                <th className="px-4 py-2 text-right">Écarts (-)</th>
                <th className="px-4 py-2 text-center">Fiabilité</th>
                <th className="px-4 py-2 text-right">Dette</th>
              </tr>
            </thead>
            <tbody>
              {(data.performance || []).map(v => (
                <tr key={v.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <p className="font-medium text-gray-800">{v.prenom} {v.nom}</p>
                    <p className="text-xs text-gray-400">{v.telephone}</p>
                  </td>
                  <td className="px-4 py-2 text-center">{v.jours_travailles}</td>
                  <td className="px-4 py-2 text-right">{fmt(v.montant_attendu_total)}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmt(v.montant_remis_total)}</td>
                  <td className="px-4 py-2 text-right text-red-600">{v.ecarts_negatifs > 0 ? `-${fmt(v.ecarts_negatifs)}` : '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      v.taux_fiabilite >= 95 ? 'bg-green-100 text-green-700' :
                      v.taux_fiabilite >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>{v.taux_fiabilite}%</span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    {v.solde_dette > 0
                      ? <span className="text-red-600 font-semibold">{fmt(v.solde_dette)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Onglet SYNTHÈSE — vue simplifiée pour profane ─────────────
// C'est l'onglet PAR DÉFAUT : il consolide les chiffres essentiels avec
// un résumé en langage naturel, des codes couleur clairs et la détection
// automatique des anomalies.
function OngletSynthese({ debut, fin }) {
  const { moduleActif } = useAuth();
  const aTerrainS = moduleActif('vendeurs_terrain');
  const aLigneS   = moduleActif('commandes_ligne');
  const [data, setData] = useState(null);
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/rapports/synthese?date_debut=${debut}&date_fin=${fin}`),
      api.get(`/rapports/stock-valeur`),
    ])
      .then(([s, st]) => { setData(s.data); setStock(st.data); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [debut, fin]);

  if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;
  if (!data)   return <div className="text-center py-20 text-red-400">Erreur de chargement</div>;

  // Couleur du bénéfice
  const beneficeColor =
    data.beneficeReel > 0 ? 'green' :
    data.beneficeReel === 0 ? 'amber' :
    'red';
  const beneficeBg =
    data.beneficeReel > 0 ? 'bg-green-50 border-green-300' :
    data.beneficeReel === 0 ? 'bg-amber-50 border-amber-300' :
    'bg-red-50 border-red-300';
  const beneficeText =
    data.beneficeReel > 0 ? 'text-green-700' :
    data.beneficeReel === 0 ? 'text-amber-700' :
    'text-red-700';

  // Couleur d'une anomalie selon son type
  const anomalieStyle = (type) => ({
    critique:  'bg-red-50    border-red-300    text-red-800',
    attention: 'bg-amber-50  border-amber-300  text-amber-800',
    info:      'bg-blue-50   border-blue-300   text-blue-800',
  }[type] || 'bg-gray-50 border-gray-300 text-gray-800');

  return (
    <div className="space-y-6">
      {/* ═══════ 1. RÉSUMÉ EN LANGAGE SIMPLE ═══════ */}
      <div className={`rounded-2xl border-2 p-5 ${beneficeBg}`}>
        <div className="flex items-start gap-3">
          <span className="text-3xl">{data.beneficeReel >= 0 ? '✅' : '⚠️'}</span>
          <div className="flex-1">
            <h3 className={`font-bold text-base mb-1 ${beneficeText}`}>En résumé</h3>
            <p className={`text-sm leading-relaxed ${beneficeText}`}>{data.resume}</p>
          </div>
        </div>
      </div>

      {/* ═══════ 2. INDICATEURS PRINCIPAUX ═══════ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Ventes"
          value={`${fmt(data.ventes)}`} color="indigo" />
        <StatCard label="Coût marchandises vendues"
          value={`${fmt(data.cogs)}`}
          sub="Ce qu'elles t'ont coûté à l'achat"
          color="amber" />
        <StatCard label="Dépenses (transport, salaires…)"
          value={`${fmt(data.depenses)}`} color="red" />
        <StatCard
          label="Bénéfice réel"
          value={`${fmt(data.beneficeReel)}`}
          sub={`Marge nette : ${data.margeNettePct}%`}
          color={beneficeColor} />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
        💡 <strong>Bénéfice réel</strong> = Ventes − Coût des marchandises vendues − Dépenses opérationnelles.
        Les <strong>achats fournisseurs ({fmt(data.achatsStock)} sur la période)</strong> ne sont pas comptés
        comme une dépense — c'est un investissement qui alimente ton stock.
      </div>

      {/* ═══════ 3. ANOMALIES DÉTECTÉES ═══════ */}
      {data.anomalies && data.anomalies.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            🔍 Points d'attention
          </h3>
          {data.anomalies.map((a, i) => (
            <div key={i} className={`rounded-xl border p-3 ${anomalieStyle(a.type)}`}>
              <div className="flex items-start gap-2">
                <span className="text-base">
                  {a.type === 'critique' ? '🔴' : a.type === 'attention' ? '🟡' : 'ℹ️'}
                </span>
                <div className="flex-1 text-sm">
                  <p className="font-bold">{a.titre}</p>
                  <p className="mt-0.5 opacity-90">{a.message}</p>
                  {a.action && <p className="mt-1 italic opacity-80">→ {a.action}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══════ 4. RÉPARTITION DES VENTES PAR CANAL ═══════ */}
      <div className="bg-white rounded-xl border p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Répartition des ventes par canal</h3>
        <div className="space-y-2">
          {[
            { label: 'Magasin',         val: data.ventesParCanal.magasin,   color: 'indigo', visible: true },
            { label: 'Vendeurs terrain', val: data.ventesParCanal.terrain,   color: 'amber',  visible: aTerrainS },
            { label: '🌐 Commandes ligne',  val: data.ventesParCanal.livraison, color: 'green',  visible: aLigneS },
          ].filter(c => c.visible).map(c => (
            <div key={c.label}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">{c.label}</span>
                <span className="font-semibold text-gray-900">
                  {fmt(c.val)}
                  {data.ventes > 0 && (
                    <span className="text-xs text-gray-400 ml-2">({Math.round(c.val/data.ventes*100)}%)</span>
                  )}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${
                  c.color === 'indigo' ? 'bg-indigo-500' :
                  c.color === 'amber' ? 'bg-amber-500' : 'bg-green-500'
                }`} style={{ width: `${data.ventes > 0 ? (c.val/data.ventes*100) : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════ 5. STOCK (situation actuelle, pas la période) ═══════ */}
      {stock && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Situation du stock (aujourd'hui)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Stock magasin</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{fmt(stock.valeur_stock_magasin)}</p>
              <p className="text-xs text-gray-400 mt-1">{fmt(stock.qte_disponible)} articles</p>
            </div>
            {aTerrainS && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Stock chez vendeurs</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{fmt(stock.valeur_chez_vendeurs)}</p>
                <p className="text-xs text-gray-400 mt-1">{fmt(stock.qte_chez_vendeurs)} articles confiés</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Total immobilisé</p>
              <p className="text-lg font-bold text-indigo-700 mt-1">
                {fmt(stock.valeur_stock_total + stock.valeur_chez_vendeurs)}
              </p>
              <p className="text-xs text-gray-400 mt-1">{aTerrainS ? 'Magasin + chez vendeurs' : 'Stock magasin'}</p>
            </div>
            <div className={`rounded-lg p-3 ${
              stock.nb_ruptures > 0 ? 'bg-red-50 border border-red-200' :
              stock.nb_alertes_basses > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'
            }`}>
              <p className="text-xs text-gray-500">Alertes</p>
              <p className="text-lg font-bold mt-1">
                {stock.nb_ruptures > 0
                  ? <span className="text-red-700">{stock.nb_ruptures} rupture(s)</span>
                  : stock.nb_alertes_basses > 0
                    ? <span className="text-amber-700">{stock.nb_alertes_basses} alerte(s)</span>
                    : <span className="text-green-700">Tout va bien</span>}
              </p>
              {stock.nb_alertes_basses > 0 && stock.nb_ruptures === 0 && (
                <p className="text-xs text-gray-400 mt-1">{stock.nb_alertes_basses} stock(s) bas</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ 6. PERFORMANCE VENDEURS ═══════ */}
      {aTerrainS && data.vendeurs && (data.vendeurs.total_dettes > 0 || data.vendeurs.nb_sorties_souffrance > 0) && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Situation des vendeurs</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700">Dettes en cours</p>
              <p className="text-lg font-bold text-amber-900 mt-1">{fmt(data.vendeurs.total_dettes)}</p>
              <p className="text-xs text-amber-600 mt-1">{data.vendeurs.nb_avec_dette} vendeur(s) concerné(s)</p>
            </div>
            {data.vendeurs.nb_sorties_souffrance > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs text-red-700">Sorties en souffrance</p>
                <p className="text-lg font-bold text-red-900 mt-1">{data.vendeurs.nb_sorties_souffrance}</p>
                <p className="text-xs text-red-600 mt-1">À régulariser d'urgence</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════ 7. DÉPENSES DÉTAILLÉES ═══════ */}
      {data.depensesDetail && data.depensesDetail.length > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">💸 Détail des dépenses</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase border-b">
                <tr>
                  <th className="text-left py-2">Poste</th>
                  <th className="text-center py-2">Nb</th>
                  <th className="text-right py-2">Montant</th>
                  <th className="text-right py-2 hidden md:table-cell">% du total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.depensesDetail.map((d, i) => (
                  <tr key={i}>
                    <td className="py-2 text-gray-800">{d.description}</td>
                    <td className="py-2 text-center text-gray-500">{d.nb}</td>
                    <td className="py-2 text-right font-semibold text-red-600">{fmt(d.montant)}</td>
                    <td className="py-2 text-right text-gray-400 hidden md:table-cell">
                      {data.depenses > 0 ? Math.round(d.montant / data.depenses * 100) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Rentabilité (garde-fou) ────────────────────────────
function OngletRentabilite({ debut, fin }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/rapports/rentabilite?date_debut=${debut}&date_fin=${fin}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [debut, fin]);

  if (loading) return <div className="text-center py-12 text-gray-400">Chargement…</div>;
  if (!data)   return <div className="text-center py-12 text-gray-400">Aucune donnée</div>;

  const canalLabel = { magasin: 'Magasin', en_ligne: 'En ligne', vendeur: 'Vendeur' };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500">Ventes à perte (période)</p>
          <p className="text-2xl font-black text-red-600">{data.nb_ventes_perte}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500">Perte totale</p>
          <p className="text-2xl font-black text-red-600">{fmt(data.perte_totale)}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500">Produits à marge faible (&lt; {data.seuil}%)</p>
          <p className="text-2xl font-black text-amber-600">{data.nb_marges_faibles}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Ventes sous le coût d'achat</h3>
        {data.ventes_perte.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune vente à perte sur la période. 👍</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Canal</th>
                  <th className="py-2 pr-3">Article</th>
                  <th className="py-2 pr-3 text-right">Prix</th>
                  <th className="py-2 pr-3 text-right">Coût</th>
                  <th className="py-2 pr-3 text-right">Qté</th>
                  <th className="py-2 text-right">Perte</th>
                </tr>
              </thead>
              <tbody>
                {data.ventes_perte.map((l, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">{String(l.date).slice(0, 10)}</td>
                    <td className="py-2 pr-3 text-gray-600">{canalLabel[l.canal] || l.canal}</td>
                    <td className="py-2 pr-3 text-gray-800">{l.libelle}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmt(l.prix_unitaire)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{fmt(l.cout_unitaire)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.quantite}</td>
                    <td className="py-2 text-right tabular-nums font-semibold text-red-600">-{fmt(l.perte_totale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Produits à marge faible (catalogue)</h3>
        {data.marges_faibles.length === 0 ? (
          <p className="text-sm text-gray-400">Aucun produit sous le seuil de {data.seuil}%.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">Article</th>
                  <th className="py-2 pr-3 text-right">Prix</th>
                  <th className="py-2 pr-3 text-right">Coût (PAM)</th>
                  <th className="py-2 text-right">Marge</th>
                </tr>
              </thead>
              <tbody>
                {data.marges_faibles.map((l, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-gray-800">{l.libelle}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmt(l.prix)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{fmt(l.cout)}</td>
                    <td className={`py-2 text-right tabular-nums font-semibold ${Number(l.taux) < 0 ? 'text-red-600' : 'text-amber-600'}`}>{l.taux}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function Rapports() {
  const { moduleActif } = useAuth();
  // Rapports dynamiques : onglets/canaux des modules désactivés masqués
  const aTerrain = moduleActif('vendeurs_terrain');
  const [onglet, setOnglet] = useState('synthese');
  const [debut,  setDebut]  = useState(monthStart());
  const [fin,    setFin]    = useState(today());

  const handleDate = useCallback((key, val) => {
    if (key === 'debut') setDebut(val); else setFin(val);
  }, []);

  // DateRangePicker fournit déjà ses presets (Aujourd'hui/Hier/7j/30j/...) —
  // pas besoin de PeriodPills séparés. Le bouton refresh remet à Aujourd'hui.
  const refreshAll = () => {
    const t = getPeriodRange('today');
    setDebut(t.from);
    setFin(t.to);
  };

  const onglets = [
    { id: 'synthese', label: '🎯 Synthèse' },
    { id: 'ca',       label: "Chiffre d'affaires" },
    { id: 'benefice', label: 'Bénéfice détaillé' },
    { id: 'produits', label: 'Produits' },
    { id: 'rentabilite', label: '⚠️ Rentabilité' },
    // Performance vendeurs : seulement si le module terrain est actif
    ...(aTerrain ? [{ id: 'vendeurs', label: 'Vendeurs/Rabateurs' }] : []),
  ];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Rapports & Statistiques</h1>
          <p className="text-gray-500 text-sm mt-1">Analysez vos performances et téléchargez en PDF, Excel, CSV ou Word</p>
        </div>
        <button
          onClick={refreshAll}
          className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-all flex-shrink-0"
          title="Actualiser (remet à Aujourd'hui)"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="bg-white rounded-xl border p-4">
        <DateFilter debut={debut} fin={fin} onChange={handleDate} />
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit flex-wrap">
        {onglets.map(o => (
          <button key={o.id} onClick={() => setOnglet(o.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              onglet === o.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {o.label}
          </button>
        ))}
      </div>

      {onglet === 'synthese' && <OngletSynthese debut={debut} fin={fin} />}
      {onglet === 'ca'       && <OngletCA       debut={debut} fin={fin} />}
      {onglet === 'benefice' && <OngletBenefice debut={debut} fin={fin} />}
      {onglet === 'produits' && <OngletProduits debut={debut} fin={fin} />}
      {onglet === 'vendeurs' && aTerrain && <OngletVendeurs debut={debut} fin={fin} />}
    </div>
  );
}
